import { useCallback, useContext, useMemo } from "react";
import classNames from "classnames";
import { useLocation, useNavigate } from "react-router-dom";
import { parseQueries } from "app/utils/routerUtils";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import RelativeLink from "app/pages/.shared/RelativeLink";
import Paragraphs from "app/pages/.shared/Paragraphs";
import LogoBrandContainer from "app/pages/.shared/LogoBrand/LogoBrandContainer";
import AppGlobalsContext from "app/AppGlobalsContext";
import { BRANDS, STRATEGY, BG_GRADIENT_ANIMATION } from "app/constants";
import AuthLayerContainer from "app/pages/Auth/AuthLayer/AuthLayerContainer";
import "./AuthBookingLayer.scss";
import { strategyShape } from "app/utils/propTypes";
import ReasonToBookBlockContainer from "app/pages/.shared/ReasonsToBook/ReasonToBookBlock/ReasonToBookBlockContainer";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import BackgroundGradient from "app/pages/.shared/BackgroundGradient/BackgroundGradient";
import { ArrowLeftIcon, ArrowRightIcon } from "app/icons/navigation";
import { ICON_SIZES } from "app/constants";

const AuthBookingLayer = ({
	fallbackRedirectUrl = "/",
	showSigninByDefault = true,
	bookingAuthLandingText = {},
	strategy,
}) => {
	const { signinTitle, signinDescription, signupTitle, signupDescription } =
		bookingAuthLandingText;
	const navigate = useNavigate();
	const { search } = useLocation();
	const query = parseQueries(search);

	const onAuthSuccess = useCallback(() => {
		if (query.onauthsuccess) {
			navigate(query.onauthsuccess, {
				replace: true,
			});
		} else {
			navigate("/");
		}
	}, [query.onauthsuccess]);

	const { brand } = useContext(AppGlobalsContext);

	const isBrandEH = brand === BRANDS.EK;

	const authFooter = useMemo(
		() => (
			<div className="auth-booking-layer__footer">
				<RelativeLink className="nav__item-link" to={{ pathname: "/booking-search" }}>
					<FormattedMessage id={"booking.auth.footer"} />
					<ArrowRightIcon size={ICON_SIZES.XS} />
				</RelativeLink>
			</div>
		),
		[]
	);

	const signinMarketingBlock = useMemo(
		() => (
			<div className="booking-auth__marketing">
				<div className="booking-auth__marketing-content">
					{signinTitle && signinTitle.length > 0 && (
						<Typography
							variant={TYPOGRAPHY_VARIANTS.XL3}
							variantMD={TYPOGRAPHY_VARIANTS.XL5}
							isBold
							className="booking-auth__marketing-title"
							component="h2"
						>
							<Paragraphs paragraphs={signinTitle} enableHTMLFormatting />
						</Typography>
					)}
					{signinDescription && signinDescription.length > 0 && (
						<div className="booking-auth__marketing-description">
							<Paragraphs paragraphs={signinDescription} enableHTMLFormatting />
						</div>
					)}
				</div>
			</div>
		),
		[bookingAuthLandingText]
	);

	const signupMarketingBlock = useMemo(
		() => (
			<div className="booking-auth__marketing">
				<div className="booking-auth__marketing-content">
					{signupTitle && signupTitle.length > 0 && (
						<Typography
							variant={TYPOGRAPHY_VARIANTS.XL3}
							variantMD={TYPOGRAPHY_VARIANTS.XL5}
							isBold
							className="booking-auth__marketing-title"
							component="h2"
						>
							<Paragraphs paragraphs={signupTitle} enableHTMLFormatting />
						</Typography>
					)}
					{signupDescription && signupDescription.length > 0 && (
						<div className="booking-auth__marketing-description">
							<Paragraphs paragraphs={signupDescription} enableHTMLFormatting />
						</div>
					)}
				</div>
			</div>
		),
		[bookingAuthLandingText]
	);

	return (
		<div className="auth-booking-layer" data-testid="auth-layer">
			<div className="auth-booking-layer__header">
				<div
					className="auth-booking-layer__header-back"
					onClick={() => {
						window.history.back();
					}}
				>
					<ArrowLeftIcon size={ICON_SIZES.LG} />
				</div>
				<RelativeLink
					to={{ pathname: fallbackRedirectUrl }}
					className={classNames({
						"auth-booking-layer__header__logo": true,
						"auth-booking-layer__header__logo-small": isBrandEH,
					})}
				>
					<LogoBrandContainer />
				</RelativeLink>
			</div>
			<div className="booking-auth">
				<BackgroundGradient animation={BG_GRADIENT_ANIMATION.NONE} />
				<div className="booking-auth__content">
					<AuthLayerContainer
						brand={brand}
						isDefaultSignin={showSigninByDefault}
						onAuthSuccess={onAuthSuccess}
						signinMarketingBlock={signinMarketingBlock}
						signupMarketingBlock={signupMarketingBlock}
						{...(strategy !== STRATEGY.SIGNUP_FIRST && { signupFooter: authFooter })}
						{...(strategy !== STRATEGY.SIGNUP_FIRST && { signinFooter: authFooter })}
					/>
				</div>
			</div>
			<div className="auth-booking-layer__rtb">
				<ReasonToBookBlockContainer />
			</div>
		</div>
	);
};

AuthBookingLayer.propTypes = {
	fallbackRedirectUrl: PropTypes.string,
	showSigninByDefault: PropTypes.bool,
	bookingAuthLandingText: PropTypes.object,
	strategy: strategyShape,
};

export default AuthBookingLayer;
